import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../services/authContext';
import { BookOpen, Sparkles, Code, MessageCircle, ArrowRight, GraduationCap } from 'lucide-react';

export default function Home() {
  const { isAuthenticated, user } = useAuth();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-32">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-apple-sm mb-6">
              <Sparkles className="w-4 h-4 text-apple-purple" />
              <span className="text-sm text-apple-text">AI-Powered Learning Experience</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-apple-text mb-6 tracking-tight">
              Master Data Science with{' '}
              <span className="bg-gradient-to-r from-apple-blue to-apple-purple bg-clip-text text-transparent">
                AI Guidance
              </span>
            </h1>
            
            <p className="text-xl text-apple-text-secondary mb-10 leading-relaxed">
              Experience a revolutionary way to learn Data Science. 
              Interactive HTML lessons, AI-powered tutoring, and hands-on coding exercises 
              all in one elegant platform.
            </p>

            <div className="flex items-center justify-center gap-4">
              {isAuthenticated ? (
                <Link
                  to={user?.role === 'teacher' ? '/teacher' : '/courses'}
                  className="btn-primary flex items-center gap-2 text-lg px-8 py-4"
                >
                  {user?.role === 'teacher' ? 'Go to Dashboard' : 'Start Learning'}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="btn-primary flex items-center gap-2 text-lg px-8 py-4"
                  >
                    Get Started Free
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/login"
                    className="btn-outline text-lg px-8 py-4"
                  >
                    Sign In
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-1/4 left-10 w-20 h-20 bg-blue-200 rounded-full opacity-20 blur-xl" />
        <div className="absolute top-1/3 right-10 w-32 h-32 bg-purple-200 rounded-full opacity-20 blur-xl" />
        <div className="absolute bottom-1/4 left-1/4 w-24 h-24 bg-green-200 rounded-full opacity-20 blur-xl" />
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-apple-text mb-4">
              Everything you need to learn Data Science
            </h2>
            <p className="text-apple-text-secondary max-w-2xl mx-auto">
              Our platform combines cutting-edge AI technology with thoughtfully designed 
              learning experiences.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card group">
              <div className="w-12 h-12 bg-blue-100 rounded-apple-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <BookOpen className="w-6 h-6 text-apple-blue" />
              </div>
              <h3 className="text-xl font-semibold text-apple-text mb-2">
                Rich HTML Lessons
              </h3>
              <p className="text-apple-text-secondary">
                Say goodbye to boring PPTs. Learn from interactive, beautifully designed 
                HTML content with code examples and visualizations.
              </p>
            </div>

            <div className="card group">
              <div className="w-12 h-12 bg-purple-100 rounded-apple-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <MessageCircle className="w-6 h-6 text-apple-purple" />
              </div>
              <h3 className="text-xl font-semibold text-apple-text mb-2">
                AI Tutor
              </h3>
              <p className="text-apple-text-secondary">
                Stuck on a concept? Simply select any text and ask our AI tutor. 
                Get personalized explanations in real-time.
              </p>
            </div>

            <div className="card group">
              <div className="w-12 h-12 bg-green-100 rounded-apple-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Code className="w-6 h-6 text-apple-green" />
              </div>
              <h3 className="text-xl font-semibold text-apple-text mb-2">
                Hands-on Coding
              </h3>
              <p className="text-apple-text-secondary">
                Practice what you learn with interactive coding exercises. 
                Write and execute Python code directly in your browser.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Teachers Section */}
      <section className="py-24 bg-apple-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-apple-sm mb-6">
                <GraduationCap className="w-4 h-4 text-apple-orange" />
                <span className="text-sm text-apple-text">For Educators</span>
              </div>
              <h2 className="text-3xl font-bold text-apple-text mb-4">
                Design Courses with AI Assistance
              </h2>
              <p className="text-apple-text-secondary mb-6 leading-relaxed">
                Teachers can effortlessly create comprehensive Data Science courses. 
                Simply provide an outline, and our AI will generate detailed, 
                interactive HTML content. Edit and refine directly in the browser.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'AI-powered content generation from outlines',
                  'Real-time collaborative editing',
                  'Built-in quiz and exercise creation',
                  'Student progress tracking',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-apple-text">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-apple-green" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              {isAuthenticated && user?.role === 'teacher' ? (
                <Link to="/teacher" className="btn-primary">
                  Go to Teacher Dashboard
                </Link>
              ) : (
                <Link to="/register" className="btn-primary">
                  Start Teaching
                </Link>
              )}
            </div>
            <div className="flex-1">
              <div className="relative">
                <div className="bg-white rounded-apple shadow-apple p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-100 rounded w-3/4" />
                    <div className="h-4 bg-gray-100 rounded w-1/2" />
                    <div className="h-20 bg-blue-50 rounded-lg mt-4" />
                    <div className="h-4 bg-gray-100 rounded w-5/6" />
                    <div className="h-4 bg-gray-100 rounded w-2/3" />
                  </div>
                </div>
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-apple-blue to-apple-purple rounded-apple p-4 shadow-apple">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-apple-border py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-apple-blue to-apple-purple rounded-lg flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-apple-text">Data Science 101</span>
            </div>
            <p className="text-sm text-apple-text-secondary">
              © 2024 Data Science 101. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
